import { useEffect, useState } from 'react';

export interface UseLoadGoogleScriptResult {
  /** 脚本是否已加载成功 */
  loaded: boolean;
  /** 加载错误信息 */
  error: string | null;
}

/**
 * Google SDK 脚本加载 Hook
 * 优先从 CDN 加载，失败后降级到 Google 官方源
 * @returns {UseLoadGoogleScriptResult} 加载状态
 */
export function useLoadGoogleScript(): UseLoadGoogleScriptResult {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 如果已经加载过，直接返回
    if (window.google?.accounts) {
      setLoaded(true);
      return;
    }

    const onload = () => {
      setLoaded(true);
      setError(null);
    };

    const onerror = (errorMessage: string) => {
      setError(errorMessage);
      console.error('[useLoadGoogleScript]', errorMessage);
    };

    const loadOfficial = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = onload;
      script.onerror = () => onerror('Failed to load Google SDK from official source');
      document.body.appendChild(script);
    };

    // 首先尝试从 CDN 加载
    const script = document.createElement('script');
    script.src = 'https://cdn.wegic.ai/static/js/gsi-client.js';
    script.async = true;
    script.defer = true;
    script.onload = onload;
    script.onerror = () => {
      console.warn('[useLoadGoogleScript] CDN failed, falling back to official source');
      loadOfficial();
    };
    document.body.appendChild(script);
  }, []);

  return { loaded, error };
}
